import { useEffect, useState } from "react";
import { getPhoneContext, type NumberContext } from "../../../lib/phoneContext";

/**
 * How long the number has to stop changing before we ask about it.
 *
 * The pad's field changes on every keypress and a dialable number is reached
 * several digits before the operator has finished typing (+372 5551 is valid,
 * so is +372 55512, so is +372 555123). Without this, typing one Estonian mobile
 * number is five requests, four of them about somebody else.
 */
const SETTLE_MS = 350;

export interface PhoneContextState {
  context: NumberContext | null;
  loading: boolean;
}

/**
 * What we know about the number in front of the operator, or nothing.
 *
 * Nothing is a first-class answer here rather than an error: most numbers on a
 * cold list belong to nobody we have a row for, and a panel that said so loudly
 * would be shouting on almost every call. The caller draws the empty case as
 * empty; only `loading` distinguishes "we have not asked yet" from "we asked
 * and there is nothing", and only the panel decides whether that is worth a line.
 *
 * `enabled` exists so the two callers can hold the same rule in one place: an
 * operator without `use:crm.calls` has no dial pad to type into, and a panel
 * mounted while the call window is closed would poll about a stale number.
 */
export function usePhoneContext(number: string | null, enabled = true): PhoneContextState {
  const [context, setContext] = useState<NumberContext | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!number || !enabled) {
      setContext(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    const timer = setTimeout(() => {
      getPhoneContext(number)
        .then((data) => {
          if (!alive) return;
          // Guarded on the number the SERVER answered about, not on the one we
          // asked with: an answer that arrives after the operator has typed one
          // more digit describes the wrong company, and a briefing about the
          // wrong company is worse than none.
          if (data.number !== number) return;
          setContext(data);
        })
        .catch(() => {
          if (alive) setContext(null);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, SETTLE_MS);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [number, enabled]);

  return { context, loading };
}
